// Backend API service for saved jobs
import { Job } from '../../types/JobTypes';
import { sessionService } from '../session.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface SavedJob extends Job {
  savedAt: Date;
  notes?: string;
}

class SavedJobsAPI {
  private baseUrl = `${API_BASE_URL}/saved-jobs`;

  // Get all saved jobs for a user
  async getSavedJobs(userId: string): Promise<SavedJob[]> {
    const token = sessionService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/user/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch saved jobs');
    }

    return response.json();
  }

  // Save a job
  async saveJob(userId: string, job: Job, notes?: string): Promise<SavedJob> {
    const token = sessionService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        job,
        notes
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save job');
    }

    return response.json();
  }

  // Remove a saved job
  async removeSavedJob(userId: string, jobId: string): Promise<void> {
    const token = sessionService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/${jobId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId })
    });

    if (!response.ok) {
      throw new Error('Failed to remove saved job');
    }
  }

  // Update notes for a saved job
  async updateJobNotes(userId: string, jobId: string, notes: string): Promise<SavedJob> {
    const token = sessionService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/${jobId}/notes`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        notes
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update job notes');
    }

    return response.json();
  }

  // Check if a job is saved
  async isJobSaved(userId: string, jobId: string): Promise<boolean> {
    const token = sessionService.getAuthToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/${jobId}/check?userId=${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to check saved status');
    }

    const result = await response.json();
    return result.isSaved;
  }
}

export const savedJobsAPI = new SavedJobsAPI();